import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface ApiKeys {
  twelvedata: string | null;
  coinmarketcap: string | null;
  alphavantage: string | null;
  finnhub: string | null;
  marketstack: string | null;
  financialmodelingprep: string | null;
}

interface ExchangeRates {
  USD_HKD: number | null; // 美元/港元汇率
  USD_CNY: number | null; // 美元/人民币汇率
}

interface SettingsState {
  updateInterval: number; // 秒
  autoStart: boolean;
  language: 'zh' | 'en';
  twelvedataApiKey: string | null; // 保持向后兼容
  apiKeys: ApiKeys; // 所有 API key
  aktoolsBaseUrl: string | null; // Aktools base URL 配置
  exchangeRates: ExchangeRates; // 汇率配置
  // Window management settings
  autoHideEnabled: boolean; // Enable/disable auto-hide behavior
  edgeThreshold: number; // Pixels from edge to trigger show (0-100)
  animationDuration: number; // Animation duration in ms (100-1000)
  animationsEnabled: boolean; // Enable/disable animations
  setUpdateInterval: (interval: number) => void;
  setAutoStart: (enabled: boolean) => void;
  setLanguage: (lang: 'zh' | 'en') => void;
  setTwelvedataApiKey: (key: string | null) => void; // 保持向后兼容
  setApiKey: (provider: keyof ApiKeys, key: string | null) => void;
  setAktoolsBaseUrl: (url: string | null) => void;
  setExchangeRate: (pair: 'USD_HKD' | 'USD_CNY', rate: number | null) => void;
  setAutoHideEnabled: (enabled: boolean) => void;
  setEdgeThreshold: (threshold: number) => void;
  setAnimationDuration: (duration: number) => void;
  setAnimationsEnabled: (enabled: boolean) => void;
}

const defaultApiKeys: ApiKeys = {
  twelvedata: null,
  coinmarketcap: null,
  alphavantage: null,
  finnhub: null,
  marketstack: null,
  financialmodelingprep: null,
};

export const useSettingsStore = create<SettingsState>()(
  persist(
    (set, get) => ({
      updateInterval: 30,
      autoStart: false,
      language: 'zh',
      twelvedataApiKey: null, // 保持向后兼容
      apiKeys: defaultApiKeys,
      aktoolsBaseUrl: null, // Aktools base URL 配置
      exchangeRates: {
        USD_HKD: null,
        USD_CNY: null,
      },
      // Window management defaults
      autoHideEnabled: true,
      edgeThreshold: 10, // 10 pixels from edge
      animationDuration: 300, // 300ms fade
      animationsEnabled: true,
      setUpdateInterval: (interval) => set({ updateInterval: interval }),
      setAutoStart: (enabled) => set({ autoStart: enabled }),
      setLanguage: (lang) => set({ language: lang }),
      setTwelvedataApiKey: (key) => {
        // 同时更新 twelvedataApiKey 和 apiKeys.twelvedata 以保持兼容
        const currentApiKeys = get().apiKeys || defaultApiKeys;
        set({ 
          twelvedataApiKey: key,
          apiKeys: { ...currentApiKeys, twelvedata: key }
        });
      },
      setApiKey: (provider, key) => {
        const currentApiKeys = get().apiKeys || defaultApiKeys;
        const newApiKeys = { ...currentApiKeys, [provider]: key };
        set({ apiKeys: newApiKeys });
        // 如果是 twelvedata，同时更新向后兼容字段
        if (provider === 'twelvedata') {
          set({ twelvedataApiKey: key });
        }
      },
      setAktoolsBaseUrl: (url) => set({ aktoolsBaseUrl: url }),
      setExchangeRate: (pair, rate) => {
        const currentRates = get().exchangeRates;
        set({
          exchangeRates: {
            ...currentRates,
            [pair]: rate,
          },
        });
      },
      setAutoHideEnabled: (enabled) => set({ autoHideEnabled: enabled }),
      setEdgeThreshold: (threshold) => set({ edgeThreshold: threshold }),
      setAnimationDuration: (duration) => set({ animationDuration: duration }),
      setAnimationsEnabled: (enabled) => set({ animationsEnabled: enabled }),
    }),
    {
      name: 'settings-storage',
      // 添加迁移函数，确保旧数据格式兼容
      migrate: (persistedState: any, version: number) => {
        // 确保 apiKeys 始终存在且是对象
        if (!persistedState || typeof persistedState !== 'object') {
          return persistedState;
        }
        
        if (!persistedState.apiKeys || typeof persistedState.apiKeys !== 'object') {
          persistedState.apiKeys = { ...defaultApiKeys };
        } else {
          // 合并现有 apiKeys 和默认值，确保所有字段都存在
          persistedState.apiKeys = { ...defaultApiKeys, ...persistedState.apiKeys };
        }
        
        // 确保 exchangeRates 始终存在
        if (!persistedState.exchangeRates || typeof persistedState.exchangeRates !== 'object') {
          persistedState.exchangeRates = {
            USD_HKD: null,
            USD_CNY: null,
          };
        }
        
        return persistedState;
      },
    }
  )
);

